// PanelMesh3D.tsx — Shared 3D panel mesh used by both PanelViewer3D and SiteViewer3D
// Renders a concrete panel as an ExtrudeGeometry with true pass-through holes
// for openings (void). Recess/proud/embed layers are additive overlays.
//
// Geometry approach:
//   THREE.Shape (panel outline) + .holes[] (openings) → ExtrudeGeometry
//   This creates real geometry with holes — you can see through openings.
//   No external CSG library needed; Shape+Extrude is core Three.js.

import { useRef, useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import type { PanelData, ShadingMode } from '../../types/panelvar';
import type { MountType } from '../../types/mountType';
import { calcMaterials } from '../../utils/materialsCalc';
import { outlineFromMp, makeTHREEShape } from '../../utils/panelGeometry';
import { getClashingEntities } from '../../utils/clashDetection';

// ── Colours ───────────────────────────────────────────────────────────────────
const CONCRETE_COLOR  = new THREE.Color(0xc8c0b8);
const WIREFRAME_COLOR = new THREE.Color(0x00a8ff);
const FEATURE_COLOR   = new THREE.Color(0xc8a520);
const BRACE_COLOR     = new THREE.Color(0xff6000);
const PICK_COLOR      = new THREE.Color(0x00cc44);
// const WELD_COLOR      = new THREE.Color(0xffcc00);  // disabled for corner-offset test
const PILASTER_COLOR  = new THREE.Color(0x9e8e78);
const SELECT_EMISSIVE = new THREE.Color(0x00aaff);
const CLASH_EMISSIVE  = new THREE.Color(0xcc0000);
const CLASH_BRIGHT    = new THREE.Color(0xff0000);
const CLASH_EDGE_COLOR = 0xff0000;
const CG_COLOR        = new THREE.Color(0xff00ff);  // magenta — distinct from picks/braces
const FOAM_COLOR      = new THREE.Color(0xd8d4d0);  // lighter warm gray — backer rod / expansion joint filler

// ── Tagged mesh data ──────────────────────────────────────────────────────────
interface TaggedBox    { entityId: string; geo: THREE.BufferGeometry; x: number; y: number; z: number }
interface TaggedEmbed  { entityId: string; cx: number; cy: number; z: number; color: THREE.Color; geo: THREE.BufferGeometry; rotX?: number }
interface OpeningHit   { entityId: string; cx: number; cy: number; w: number; h: number }

export type LayerVisibility = Record<MountType, boolean>;

export interface PanelMesh3DProps {
  panel: PanelData;
  shading: ShadingMode;
  showPanelMarks: boolean;
  /** Per-entity selection set (PanelViewer3D) — omit for site view */
  selectedEntities?: Set<string>;
  /** Whole-panel highlight (SiteViewer3D) */
  isSelected?: boolean;
  /** Spatial clash highlight (SiteViewer3D) */
  isClash?: boolean;
  layerVisibility: LayerVisibility;
  /** Joint trim from left edge (inches) — shrinks mesh to show expansion gap */
  leftTrim?: number;
  /** Joint trim from right edge (inches) — shrinks mesh to show expansion gap */
  rightTrim?: number;
}

export default function PanelMesh3D({
  panel, shading, showPanelMarks,
  selectedEntities, isSelected, isClash,
  layerVisibility, leftTrim: ltProp, rightTrim: rtProp,
}: PanelMesh3DProps) {
  const ref = useRef<THREE.Group>(null);
  const selSet = selectedEntities ?? new Set<string>();
  const mp = panel.mpvar;

  const hw = mp.width / 2;
  const hh = mp.height / 2;
  const ht = mp.thickness / 2;
  const ffeY = -(mp.botElev ?? 0) * 12;

  const isWire   = shading === 'wireframe';
  const isHidden = shading === 'hidden';

  // ── Clash detection — which entities and edges are clashing ─────────────
  const { clashEntityIds, clashEdges } = useMemo(() => {
    if (!isClash) return { clashEntityIds: new Set<string>(), clashEdges: new Set<'left' | 'right' | 'top' | 'bottom'>() };
    const result = getClashingEntities(panel);
    return { clashEntityIds: result.entityIds, clashEdges: result.edges };
  }, [isClash, panel]);

  // ── Materials ───────────────────────────────────────────────────────────────
  const panelMat = useMemo(() => new THREE.MeshStandardMaterial({
    color: CONCRETE_COLOR, roughness: 0.85, metalness: 0.02,
    wireframe: isWire, side: THREE.DoubleSide,
    opacity: isHidden ? 0.0 : 1.0, transparent: isHidden,
    emissive: isClash ? CLASH_EMISSIVE : isSelected ? SELECT_EMISSIVE : new THREE.Color(0x000000),
    emissiveIntensity: isClash ? 0.8 : isSelected ? 0.7 : 0,
  }), [isWire, isHidden, isSelected, isClash]);

  const panelMatSel = useMemo(() => new THREE.MeshStandardMaterial({
    color: CONCRETE_COLOR, roughness: 0.85, metalness: 0.02,
    wireframe: isWire, side: THREE.DoubleSide,
    opacity: isHidden ? 0.0 : 1.0, transparent: isHidden,
    emissive: SELECT_EMISSIVE, emissiveIntensity: 0.35,
  }), [isWire, isHidden]);

  const wireMat = useMemo(() =>
    new THREE.MeshBasicMaterial({ color: WIREFRAME_COLOR, wireframe: true }), []);

  // Invisible material for opening hit-test meshes (raycasting only)
  const hitTestMat = useMemo(() =>
    new THREE.MeshBasicMaterial({ opacity: 0, transparent: true, depthWrite: false, side: THREE.DoubleSide }), []);

  const featureMat = useMemo(() =>
    new THREE.MeshStandardMaterial({ color: FEATURE_COLOR, roughness: 0.95 }), []);

  const featureMatSel = useMemo(() =>
    new THREE.MeshStandardMaterial({ color: FEATURE_COLOR, roughness: 0.95, emissive: SELECT_EMISSIVE, emissiveIntensity: 0.4 }), []);

  // Bright red material for clashing openings/features
  const clashMat = useMemo(() =>
    new THREE.MeshStandardMaterial({ color: CLASH_BRIGHT, roughness: 0.5, emissive: CLASH_BRIGHT, emissiveIntensity: 0.6, transparent: true, opacity: 0.85 }), []);

  const pilasterMat = useMemo(() =>
    new THREE.MeshStandardMaterial({ color: PILASTER_COLOR, roughness: 0.8, metalness: 0.05 }), []);

  // Foam backer rod material — slightly lighter than concrete so joints are visible
  const foamMat = useMemo(() =>
    new THREE.MeshStandardMaterial({ color: FOAM_COLOR, roughness: 0.95, metalness: 0.0 }), []);

  // ── Geometry: panel body with punched openings ─────────────────────────────
  // Shape drawn (0,0)→(width,height) in XY, extruded along Z by thickness,
  // then translated to center at origin (same as BoxGeometry convention).
  const lt = ltProp ?? 0;
  const rt = rtProp ?? 0;

  const panelGeo = useMemo(() => {
    const outline = outlineFromMp(mp);

    // Collect rectangular openings for hole-punching
    const rectOpenings: Array<{ cx: number; cy: number; w: number; h: number }> = [];
    const roundOpenings: Array<{ cx: number; cy: number; r: number }> = [];

    if (layerVisibility.void) {
      panel.rovar.forEach(s => {
        if (s.enabled) rectOpenings.push({ cx: s.data.x, cy: s.data.y + ffeY, w: s.data.width, h: s.data.height });
      });
      panel.wdvar.forEach(s => {
        if (s.enabled) rectOpenings.push({ cx: s.data.x, cy: s.data.y + ffeY, w: s.data.width, h: s.data.height });
      });
      panel.drvar.forEach(s => {
        if (s.enabled) rectOpenings.push({ cx: s.data.x, cy: s.data.y + ffeY + s.data.height / 2, w: s.data.width, h: s.data.height });
      });
      panel.dlvar.forEach(s => {
        if (s.enabled) rectOpenings.push({ cx: s.data.x, cy: s.data.y + ffeY, w: s.data.width, h: s.data.height });
      });
      panel.sbvar.forEach(s => {
        if (s.enabled) rectOpenings.push({ cx: s.data.x, cy: s.data.y + ffeY, w: s.data.width, h: s.data.height });
      });
      panel.rbvar.forEach(s => {
        if (!s.enabled) return;
        roundOpenings.push({ cx: s.data.x, cy: s.data.y + ffeY, r: (s.data.diameter ?? 12) / 2 });
      });
    }

    const shape = makeTHREEShape(outline, rectOpenings, roundOpenings, mp.width, mp.height);

    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: mp.thickness,
      bevelEnabled: false,
    });
    // Center at origin — same as BoxGeometry
    geo.translate(-hw, -hh, -ht);
    return geo;
  }, [panel, mp.width, mp.height, mp.thickness, mp.heightL, mp.heightR, mp.topAngleL, mp.topAngleR, mp.topRadiusL, mp.topRadiusR, hw, hh, ht, ffeY, layerVisibility.void]);

  // ── Opening hit-test targets (for entity selection raycasting) ─────────────
  const openingHits = useMemo(() => {
    if (!layerVisibility.void) return [];
    const list: OpeningHit[] = [];
    const addHit = (grp: string, i: number, cx: number, cy: number, w: number, h: number) => {
      list.push({ entityId: `${grp}-${i}`, cx, cy, w, h });
    };
    panel.rovar.forEach((s, i) => { if (s.enabled) addHit('rovar', i, s.data.x - hw, s.data.y + ffeY - hh, s.data.width, s.data.height); });
    panel.wdvar.forEach((s, i) => { if (s.enabled) addHit('wdvar', i, s.data.x - hw, s.data.y + ffeY - hh, s.data.width, s.data.height); });
    panel.drvar.forEach((s, i) => { if (s.enabled) addHit('drvar', i, s.data.x - hw, (s.data.y + ffeY + s.data.height / 2) - hh, s.data.width, s.data.height); });
    panel.dlvar.forEach((s, i) => { if (s.enabled) addHit('dlvar', i, s.data.x - hw, s.data.y + ffeY - hh, s.data.width, s.data.height); });
    panel.sbvar.forEach((s, i) => { if (s.enabled) addHit('sbvar', i, s.data.x - hw, s.data.y + ffeY - hh, s.data.width, s.data.height); });
    panel.rbvar.forEach((s, i) => {
      if (!s.enabled) return;
      const d = s.data.diameter ?? 12;
      addHit('rbvar', i, s.data.x - hw, s.data.y + ffeY - hh, d, d);
    });
    return list;
  }, [panel, hw, hh, ffeY, layerVisibility.void]);

  // ── RECESS: feature strip recesses ─────────────────────────────────────────
  const features = useMemo(() => {
    if (!layerVisibility.recess) return [];
    const list: TaggedBox[] = [];
    panel.fhvar.forEach((s, i) => {
      if (!s.enabled) return;
      const xS = s.data.xEnd > 0 ? s.data.xStart : 0;
      const xE = s.data.xEnd > 0 ? s.data.xEnd : mp.width;
      const fw = xE - xS;
      const depth = s.data.depth || 0.5;
      // Recess: indented into exterior (+Z) face
      list.push({ entityId: `fhvar-${i}`, geo: new THREE.BoxGeometry(fw, s.data.height, depth), x: (xS + fw / 2) - hw, y: s.data.y + ffeY - hh, z: ht - depth / 2 });
    });
    panel.fvvar.forEach((s, i) => {
      if (!s.enabled) return;
      const yS = s.data.yEnd > 0 ? s.data.yStart : 0;
      const yE = s.data.yEnd > 0 ? s.data.yEnd : mp.height;
      const fh = yE - yS;
      const depth = s.data.depth || 0.5;
      list.push({ entityId: `fvvar-${i}`, geo: new THREE.BoxGeometry(s.data.width, fh, depth), x: s.data.x - hw, y: (yS + ffeY + fh / 2) - hh, z: ht - depth / 2 });
    });
    return list;
  }, [panel, hw, hh, ffeY, mp.width, mp.height, ht, layerVisibility.recess]);

  // ── PROUD: pilasters ───────────────────────────────────────────────────────
  const pilasters = useMemo(() => {
    if (!layerVisibility.proud) return [];
    const list: TaggedBox[] = [];
    panel.plvar.forEach((s, i) => {
      if (!s.enabled || !s.data.width) return;
      const d = s.data;
      const proj = d.projection || 6;   // protrusion from exterior face
      const ph = d.height > 0 ? d.height : mp.height;
      const px = d.side === 'left' ? -(d.width / 2) - hw : (d.width / 2) + hw;
      const py = (ph / 2) + ffeY - hh;
      list.push({ entityId: `plvar-${i}`, geo: new THREE.BoxGeometry(d.width, ph, proj), x: px, y: py, z: ht + proj / 2 });
    });
    return list;
  }, [panel, hw, hh, ffeY, ht, mp.height, layerVisibility.proud]);

  // ── EMBED: shaped inserts ──────────────────────────────────────────────────
  // pick points → cone, brace points → cylinder, welds → flat plate
  const embeds = useMemo(() => {
    if (!layerVisibility.embed) return [];
    const list: TaggedEmbed[] = [];
    const backZ  = -ht - 1;   // interior (back) face
    // const frontZ =  ht + 1;   // exterior (front) face — disabled while weld test active

    // Pick points — cone pointing inward (lifting lug)
    panel.ppvar.forEach((s, i) => {
      if (!s.enabled) return;
      const r = (s.data.size ?? 1.25) * 0.8;
      list.push({ entityId: `ppvar-${i}`, cx: s.data.x - hw, cy: s.data.y + ffeY - hh, z: backZ, color: PICK_COLOR, geo: new THREE.ConeGeometry(r, r * 2.5, 4), rotX: Math.PI });
    });

    // Brace points — cylinder (insert)
    panel.bpvar.forEach((s, i) => {
      if (!s.enabled) return;
      const r = (s.data.size ?? 1.25) * 0.8;
      list.push({ entityId: `bpvar-${i}`, cx: s.data.x - hw, cy: s.data.y + ffeY - hh, z: backZ, color: BRACE_COLOR, geo: new THREE.CylinderGeometry(r, r, r * 2, 12), rotX: Math.PI / 2 });
    });

    // Weld connections — DISABLED for corner-offset isolation test
    // TODO: re-enable once wall walker ignores features at panel edges
    // panel.wcvar.forEach((s, i) => {
    //   if (!s.enabled) return;
    //   const d = s.data;
    //   const wz = d.face === 'back' ? backZ : frontZ;
    //   const pw = d.plateW || 4;
    //   const ph = d.plateH || 4;
    //   const plateGeo = new THREE.BoxGeometry(pw, ph, 0.5);
    //   if (d.sideL) list.push({ entityId: `wcvar-${i}`, cx: 0 - hw, cy: d.y + ffeY - hh, z: wz, color: WELD_COLOR, geo: plateGeo });
    //   if (d.sideR) list.push({ entityId: `wcvar-${i}`, cx: mp.width - hw, cy: d.y + ffeY - hh, z: wz, color: WELD_COLOR, geo: plateGeo });
    //   if (!d.sideL && !d.sideR) list.push({ entityId: `wcvar-${i}`, cx: d.x - hw, cy: d.y + ffeY - hh, z: wz, color: WELD_COLOR, geo: plateGeo });
    // });

    return list;
  }, [panel, hw, hh, ffeY, ht, mp.width, layerVisibility.embed]);

  // ── PROUD: top plate ───────────────────────────────────────────────────────
  const topPlate = useMemo(() => {
    if (!layerVisibility.proud || !panel.tpvar.enabled) return null;
    const t = panel.tpvar.thickness || 0.5;
    return {
      geo: new THREE.BoxGeometry(mp.width, t, mp.thickness),
      y: hh + t / 2,
    };
  }, [panel.tpvar, mp.width, mp.thickness, hh, layerVisibility.proud]);

  // ── CG crosshair (interior face) ─────────────────────────────────────────
  const cgMarker = useMemo(() => {
    if (mp.width <= 0 || mp.height <= 0) return null;
    const mat = calcMaterials(panel);
    const armLen = Math.min(mp.width, mp.height) * 0.06; // crosshair arm length
    const cx = mat.cgX - hw;
    const cy = mat.cgY - hh;
    return { cx, cy, armLen };
  }, [panel, mp.width, mp.height, hw, hh]);

  // Tag group with panelId for raycasting
  useEffect(() => {
    if (ref.current) ref.current.userData.panelId = panel.id;
  }, [panel.id]);

  // ── Material selection helper ──────────────────────────────────────────────
  const pMat = (selSet.has('panel-body') || isSelected) ? (selectedEntities ? panelMatSel : panelMat) : panelMat;

  return (
    <group ref={ref} position={[hw, hh, 0]} rotation={[0, Math.PI, 0]}>
      {/* Panel body — ExtrudeGeometry with punched opening holes */}
      <mesh geometry={panelGeo} material={pMat}
        userData={selectedEntities ? { entityId: 'panel-body' } : { panelId: panel.id }} />
      {isHidden && <mesh geometry={panelGeo} material={wireMat} />}

      {/* Foam backer rod — extends outward from panel edge into the joint gap.
          Each panel owns its half of a coplanar joint (jW/2) or full butt joint.
          Two adjacent panels' foam strips meet in the middle of the gap.
          Positions are pre-flipped because the parent group has rotation Y=PI. */}
      {lt > 0 && !isHidden && (
        <mesh position={[hw + lt / 2, 0, 0]} material={foamMat}>
          <boxGeometry args={[lt, mp.height, mp.thickness]} />
        </mesh>
      )}
      {rt > 0 && !isHidden && (
        <mesh position={[-hw - rt / 2, 0, 0]} material={foamMat}>
          <boxGeometry args={[rt, mp.height, mp.thickness]} />
        </mesh>
      )}

      {/* Clash — bright red edge lines on panel edges with feature overhang */}
      {isClash && clashEdges.size > 0 && (() => {
        const edgeThick = 3;
        const edges: JSX.Element[] = [];
        if (clashEdges.has('left'))   edges.push(<mesh key="cl" position={[-hw, 0, 0]}><boxGeometry args={[edgeThick, mp.height, mp.thickness + 2]} /><meshBasicMaterial color={CLASH_EDGE_COLOR} /></mesh>);
        if (clashEdges.has('right'))  edges.push(<mesh key="cr" position={[hw, 0, 0]}><boxGeometry args={[edgeThick, mp.height, mp.thickness + 2]} /><meshBasicMaterial color={CLASH_EDGE_COLOR} /></mesh>);
        if (clashEdges.has('bottom')) edges.push(<mesh key="cb" position={[0, -hh, 0]}><boxGeometry args={[mp.width, edgeThick, mp.thickness + 2]} /><meshBasicMaterial color={CLASH_EDGE_COLOR} /></mesh>);
        if (clashEdges.has('top'))    edges.push(<mesh key="ct" position={[0, hh, 0]}><boxGeometry args={[mp.width, edgeThick, mp.thickness + 2]} /><meshBasicMaterial color={CLASH_EDGE_COLOR} /></mesh>);
        return <>{edges}</>;
      })()}

      {/* Opening hit-test targets — invisible meshes for entity selection */}
      {selectedEntities && openingHits.map(o => (
        <mesh key={o.entityId}
          position={[o.cx, o.cy, 0]}
          material={selSet.has(o.entityId) ? panelMatSel : clashEntityIds.has(o.entityId) ? clashMat : hitTestMat}
          userData={{ entityId: o.entityId }}>
          <boxGeometry args={[o.w, o.h, mp.thickness * 0.5]} />
        </mesh>
      ))}

      {/* Clash — bright red boxes filling clashing openings (site view, no selectedEntities) */}
      {!selectedEntities && isClash && openingHits.map(o => {
        if (!clashEntityIds.has(o.entityId)) return null;
        return (
          <mesh key={`clash-${o.entityId}`}
            position={[o.cx, o.cy, 0]}
            material={clashMat}>
            <boxGeometry args={[o.w, o.h, mp.thickness * 0.5]} />
          </mesh>
        );
      })}

      {/* RECESS — feature strips */}
      {features.map(f => (
        <mesh key={f.entityId} geometry={f.geo}
          material={clashEntityIds.has(f.entityId) ? clashMat : selSet.has(f.entityId) ? featureMatSel : featureMat}
          position={[f.x, f.y, f.z]}
          userData={{ entityId: f.entityId }} />
      ))}

      {/* PROUD — pilasters */}
      {pilasters.map(p => (
        <mesh key={p.entityId} geometry={p.geo}
          material={pilasterMat}
          position={[p.x, p.y, p.z]}
          userData={{ entityId: p.entityId }} />
      ))}

      {/* PROUD — top plate */}
      {topPlate && (
        <mesh geometry={topPlate.geo} position={[0, topPlate.y, 0]}
          userData={{ entityId: 'tpvar' }}>
          <meshStandardMaterial color="#777" roughness={0.6} metalness={0.3} />
        </mesh>
      )}

      {/* EMBED — shaped inserts */}
      {embeds.map(m => (
        <mesh key={m.entityId} geometry={m.geo}
          position={[m.cx, m.cy, m.z]}
          rotation={m.rotX != null ? [m.rotX, 0, 0] : undefined}
          userData={{ entityId: m.entityId }}>
          <meshStandardMaterial
            color={selSet.has(m.entityId) ? SELECT_EMISSIVE : m.color}
            roughness={0.3} metalness={0.6}
            emissive={selSet.has(m.entityId) ? SELECT_EMISSIVE : undefined}
            emissiveIntensity={selSet.has(m.entityId) ? 0.5 : 0}
          />
        </mesh>
      ))}

      {/* CG crosshair — interior (back) face, same Z as pick points */}
      {cgMarker && (
        <group position={[cgMarker.cx, cgMarker.cy, -ht - 1]}>
          {/* Horizontal arm */}
          <mesh>
            <boxGeometry args={[cgMarker.armLen * 2, 0.5, 0.3]} />
            <meshBasicMaterial color={CG_COLOR} />
          </mesh>
          {/* Vertical arm */}
          <mesh>
            <boxGeometry args={[0.5, cgMarker.armLen * 2, 0.3]} />
            <meshBasicMaterial color={CG_COLOR} />
          </mesh>
          {/* Label */}
          <Text
            position={[cgMarker.armLen + 4, cgMarker.armLen + 2, 0]}
            fontSize={Math.min(mp.width, mp.height) * 0.04}
            color="#ff00ff"
            anchorX="left"
            anchorY="bottom"
          >
            CG
          </Text>
        </group>
      )}

      {/* Panel mark — front face */}
      {showPanelMarks && mp.panelMark && (() => {
        const fs = Math.min(mp.width, mp.height) * 0.125;
        const tw = fs * mp.panelMark.length * 0.65 + fs * 0.4;
        const th = fs * 1.4;
        return (
          <group position={[0, 0, ht + 1]}>
            <mesh position={[0, 0, -0.1]}>
              <planeGeometry args={[tw, th]} />
              <meshBasicMaterial color="white" />
            </mesh>
            <Text fontSize={fs} color="#000000" anchorX="center" anchorY="middle">
              {mp.panelMark}
            </Text>
          </group>
        );
      })()}
      {/* Panel mark — back face */}
      {showPanelMarks && mp.panelMark && (() => {
        const fs = Math.min(mp.width, mp.height) * 0.125;
        const tw = fs * mp.panelMark.length * 0.65 + fs * 0.4;
        const th = fs * 1.4;
        return (
          <group position={[0, 0, -ht - 1]} rotation={[0, Math.PI, 0]}>
            <mesh position={[0, 0, -0.1]}>
              <planeGeometry args={[tw, th]} />
              <meshBasicMaterial color="white" />
            </mesh>
            <Text fontSize={fs} color="#000000" anchorX="center" anchorY="middle">
              {mp.panelMark}
            </Text>
          </group>
        );
      })()}
    </group>
  );
}
